import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as teamsApi from '~/services/api/teams';

import type { AddTeamMemberPayload, TeamMember, TeamRole } from '~/types';

/**
 * Query key factory for team members.
 */
export const teamMemberKeys = {
  all: ['team-members'] as const,
  list: (teamId: string) => [...teamMemberKeys.all, 'list', teamId] as const,
  lists: () => [...teamMemberKeys.all, 'list'] as const,
};

/**
 * Add a member to a team.
 */
export function useAddTeamMember(): ReturnType<
  typeof useMutation<TeamMember, Error, { data: AddTeamMemberPayload; teamId: string }>
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, teamId }) => teamsApi.addTeamMember(teamId, data),
    onSuccess: (_data, { teamId }) => {
      void queryClient.invalidateQueries({ queryKey: teamMemberKeys.list(teamId) });
    },
  });
}

/**
 * Remove a member from a team.
 */
export function useRemoveTeamMember(): ReturnType<
  typeof useMutation<void, Error, { teamId: string; userId: string }>
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ teamId, userId }) => teamsApi.removeTeamMember(teamId, userId),
    onSuccess: (_data, { teamId }) => {
      void queryClient.invalidateQueries({ queryKey: teamMemberKeys.list(teamId) });
    },
  });
}

/**
 * Fetch members for a team.
 */
export function useTeamMembers(teamId: string | undefined): {
  error: unknown;
  isLoading: boolean;
  members: TeamMember[];
  total: number;
} {
  const { data, error, isLoading } = useQuery({
    queryKey: teamMemberKeys.list(teamId ?? ''),
    queryFn: async () => {
      if (!teamId) {
        return { members: [], total: 0, limit: 100, offset: 0, has_more: false };
      }
      return teamsApi.getTeamMembers(teamId);
    },
    enabled: !!teamId,
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    error,
    isLoading,
    members: data?.members ?? [],
    total: data?.total ?? 0,
  };
}

/**
 * Update a team member's role.
 */
export function useUpdateTeamMemberRole(): ReturnType<
  typeof useMutation<TeamMember, Error, { role: TeamRole; teamId: string; userId: string }>
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ role, teamId, userId }) =>
      teamsApi.updateTeamMemberRole(teamId, userId, { role }),
    onSuccess: (_data, { teamId }) => {
      void queryClient.invalidateQueries({ queryKey: teamMemberKeys.list(teamId) });
    },
  });
}
